import React, { useEffect } from 'react'
import { it } from 'date-fns/locale'
import { CalendarIcon, CircleCheckIcon, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { dayjs } from '@/lib/dayjs.ts'
import { cn } from '@/lib/utils.ts'
import { useSmartForm } from '@/hooks/use-smart-form.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Calendar } from '@/components/ui/calendar.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { AdaptiveTextarea } from '@/components/adaptive-textarea.tsx'
import { useAddCalendarEvent } from '@/features/calendar/hooks/use-add-calendar-event.ts'

export interface CalendarEventDialogProps {
  isOpen?: boolean
  onOpenChange?(open: boolean): void
  onSuccess?(): void
  mode?: 'create' | 'update'
  title?: string
  description?: React.ReactNode
  date?: Date | string
  documentAttributeId: string
}

export const CalendarEventDialog: React.FC<CalendarEventDialogProps> = (
  props
) => {
  const {
    isOpen,
    onOpenChange,
    onSuccess,
    mode = 'Crea',
    title,
    description,
    date,
    documentAttributeId,
  } = props

  const form = useSmartForm<{ title: string; date: Date | string }>()
  useEffect(() => {
    form.reset({
      title,
      date,
    })
  }, [date, title])

  const addCalendarEvent = useAddCalendarEvent()

  const handleAddCalendarEventFormSubmit = async () => {
    await addCalendarEvent.mutateAsync({
      ...form.watch(),
      document_attribute_id: documentAttributeId,
    })

    const message = `Evento calendario ${mode === 'create' ? 'creato' : 'modificato'}`
    toast(message, { icon: <CircleCheckIcon /> })
    onSuccess?.()
    onOpenChange?.(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Crea' : 'Modifica'} evento calendario
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleAddCalendarEventFormSubmit)}>
            <div className="grid gap-4 py-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Titolo</FormLabel>
                    <FormControl>
                      <AdaptiveTextarea rows={1} {...field} />
                    </FormControl>
                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data</FormLabel>

                    <Popover modal>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              dayjs(field.value.toString()).format('DD/MM/YYYY')
                            ) : (
                              <span>Seleziona data</span>
                            )}
                            <CalendarIcon className="ml-auto size-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          className="z-40"
                          mode="single"
                          selected={
                            field.value
                              ? dayjs(field.value.toString()).toDate()
                              : undefined
                          }
                          defaultMonth={
                            field.value
                              ? dayjs(field.value.toString()).toDate()
                              : undefined
                          }
                          locale={it}
                          onSelect={(value) =>
                            field.onChange(
                              value
                                ? dayjs(value.toString())
                                    .tz('Europe/Rome')
                                    .format('YYYY-MM-DD')
                                : undefined
                            )
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end gap-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange?.(false)}
              >
                Annulla
              </Button>
              <Button disabled={addCalendarEvent.isPending}>
                {addCalendarEvent.isPending && (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                )}
                Salva
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
